import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import { initialState } from './initialState';
import { ApiExError, apiReponseError, apiReponseErrorIncludes } from '@shared';

type HierarchyInvitationRequestBody = {
  advisorEmail: string;
  isPreview: boolean;
  invitationId?: string;
  enabledClientAccess: boolean;
};

export type HierarchyInvitationCreatedResponse = {
  message: string;
  invitationId: string;
  invitedEmail: string;
};

export type HierarchyRelationshipDirectCreatedResponse = {
  message: string;
  subordinateAdvisorId: string;
};

export type AdvisorHierarchyState = {
  // used for providing access to subordinate advisor clients
  subordinateAdvisors: Advisor[] | null;
  status:
    | 'loading'
    | 'successfullyAddedRelationship'
    | 'successfullyDeletedRelationship'
    | 'error'
    | null;
  errorMessage: string | null;

  // Mutation state for invitation send
  invitationMutationStatus: 'idle' | 'loading' | 'success' | 'error';
  invitationMutationError: string | null;

  // Mutation state for direct relationship creation
  directRelationshipMutationStatus: 'idle' | 'loading' | 'success' | 'error';
  directRelationshipMutationError: string | null;

  // Mutation state for delete operation
  deleteMutationStatus: 'idle' | 'loading' | 'success' | 'error';
  deleteMutationError: string | null;

  // Unified relationships state (active + pending invitations) - used for fetching and displaying relationships in teams
  relationships: AdvisorHierarchyRelationship[] | null;
  relationshipsFetchStatus: 'idle' | 'loading' | 'success' | 'error';
  relationshipsFetchError: string | null;

  // Supervisor relationships (where the current advisor is the subordinate)
  supervisorAdvisorRelationships: SupervisorAdvisorRelationship[] | null;

  // Pending supervisor invitations (invitations received by the current advisor)
  pendingSupervisorInvitations: PendingSupervisorInvitation[] | null;

  // Accept response
  invitationAcceptResponse: {
    status: 'idle' | 'loading' | 'success' | 'error';
    error: string | null;
    message: string | null;
  };

  // Decline response
  invitationDeclineResponse: {
    status: 'idle' | 'loading' | 'success' | 'error';
    error: string | null;
    message: string | null;
  };
};

const advisorHierarchySlice = createSlice({
  name: 'advisorHierarchy',
  initialState: initialState.advisorHierarchy as AdvisorHierarchyState,
  reducers: {
    fetchSubordinateAdvisorsByAdvisorIdRequest(state, action: PayloadAction<AdvisorIdProps>) {},
    fetchSubordinateAdvisorsByAdvisorIdResponse(state, action: PayloadAction<Advisor[]>) {
      state.subordinateAdvisors = action.payload;
    },
    fetchSubordinateAdvisorsByAdvisorIdFailure(state, _action: PayloadAction<ApiExError>) {},
    postAdvisorHierarchyRelationshipRequest(
      state,
      _action: PayloadAction<PostAdvisorHierarchyRelationshipProps>,
    ) {
      state.status = 'loading';
      state.errorMessage = '';
    },
    postAdvisorHierarchyRelationshipResponse(
      state,
      action: PayloadAction<{ advisorId: string; action: 'create' | 'delete' }>,
    ) {
      state.status =
        action.payload.action === 'create'
          ? 'successfullyAddedRelationship'
          : 'successfullyDeletedRelationship';
      state.errorMessage = '';
    },
    postAdvisorHierarchyRelationshipFailure(state, action: PayloadAction<ApiExError>) {
      state.status = 'error';
      const errorMessage = apiReponseError(action.payload);
      if (apiReponseErrorIncludes(action.payload, 'not found')) {
        state.errorMessage = errorMessage;
      } else if (apiReponseErrorIncludes(action.payload, 'relationship already exists')) {
        state.errorMessage = 'This relationship already exists.';
      } else if (apiReponseErrorIncludes(action.payload, 'no relationship exists')) {
        state.errorMessage = 'No relationship exists between these advisors.';
      } else {
        state.errorMessage = 'An unknown error occurred. Please try again or contact support.';
      }
    },

    // Advisor hierarchy invitation
    postAdvisorHierarchyInvitationByAdvisorIdRequest(
      state,
      action: PayloadAction<HierarchyInvitationRequestBody & { advisorId: string }>,
    ) {
      state.invitationMutationStatus = 'loading';
      state.invitationMutationError = null;
    },
    postAdvisorHierarchyInvitationByAdvisorIdResponse(
      state,
      action: PayloadAction<HierarchyInvitationCreatedResponse>,
    ) {
      state.invitationMutationStatus = 'success';
      state.invitationMutationError = null;
    },
    postAdvisorHierarchyInvitationByAdvisorIdFailure(state, action: PayloadAction<ApiExError>) {
      state.invitationMutationStatus = 'error';
      state.invitationMutationError = action.payload.message || 'Failed to send invitation';
    },

    // Delete advisor hierarchy relationship
    deleteAdvisorHierarchyRelationshipByAdvisorIdRequest(
      state,
      action: PayloadAction<DeleteAdvisorHierarchyRelationshipBody & { advisorId: string }>,
    ) {
      state.deleteMutationStatus = 'loading';
      state.deleteMutationError = null;
    },
    deleteAdvisorHierarchyRelationshipByAdvisorIdResponse(
      state,
      action: PayloadAction<{ message: string }>,
    ) {
      state.deleteMutationStatus = 'success';
      state.deleteMutationError = null;
    },
    deleteAdvisorHierarchyRelationshipByAdvisorIdFailure(state, action: PayloadAction<ApiExError>) {
      state.deleteMutationStatus = 'error';
      state.deleteMutationError = action.payload.message || 'Failed to delete relationship';
    },

    // Fetch unified relationships (active + pending invitations)
    fetchAdvisorHierarchyRelationshipsByAdvisorIdRequest(
      state,
      _action: PayloadAction<AdvisorIdProps>,
    ) {
      state.relationshipsFetchStatus = 'loading';
      state.relationshipsFetchError = null;
    },
    fetchAdvisorHierarchyRelationshipsByAdvisorIdResponse(
      state,
      action: PayloadAction<AdvisorHierarchyRelationshipsResponse>,
    ) {
      state.relationships = action.payload.subordinateAdvisorRelationships;
      state.supervisorAdvisorRelationships = action.payload.supervisorAdvisorRelationships;
      state.pendingSupervisorInvitations = action.payload.pendingSupervisorInvitations;
      state.relationshipsFetchStatus = 'success';
      state.relationshipsFetchError = null;
    },
    fetchAdvisorHierarchyRelationshipsByAdvisorIdFailure(state, action: PayloadAction<ApiExError>) {
      state.relationshipsFetchStatus = 'error';
      state.relationshipsFetchError = action.payload.message || 'Failed to fetch relationships';
    },

    // Decline invitation (protected)
    postAdvisorHierarchyInvitationDeclineByInvitationIdRequest(
      state,
      _action: PayloadAction<{ invitationId: string }>,
    ) {
      state.invitationDeclineResponse.status = 'loading';
      state.invitationDeclineResponse.error = null;
    },
    postAdvisorHierarchyInvitationDeclineByInvitationIdResponse(
      state,
      { payload }: PayloadAction<{ message: string; invitationId: string; status: string }>,
    ) {
      state.invitationDeclineResponse.status = 'success';
      state.invitationDeclineResponse.message = payload.message;
      state.invitationDeclineResponse.error = null;
    },
    postAdvisorHierarchyInvitationDeclineByInvitationIdFailure(
      state,
      { payload }: PayloadAction<ApiExError>,
    ) {
      state.invitationDeclineResponse.status = 'error';
      state.invitationDeclineResponse.error = payload.message || 'Failed to decline invitation';
    },

    postAdvisorHierarchyInvitationAcceptByInvitationIdRequest(
      state,
      _action: PayloadAction<{
        invitationId: string;
        ipAddress: string | null;
        userAgent: string;
        metadata?: Record<string, any>;
      }>,
    ) {
      state.invitationAcceptResponse.status = 'loading';
      state.invitationAcceptResponse.error = null;
    },
    postAdvisorHierarchyInvitationAcceptByInvitationIdResponse(
      state,
      { payload }: PayloadAction<{ message: string; invitationId: string; status: string }>,
    ) {
      state.invitationAcceptResponse.status = 'success';
      state.invitationAcceptResponse.message = payload.message;
      state.invitationAcceptResponse.error = null;
    },
    postAdvisorHierarchyInvitationAcceptByInvitationIdFailure(
      state,
      { payload }: PayloadAction<ApiExError>,
    ) {
      state.invitationAcceptResponse.status = 'error';
      state.invitationAcceptResponse.error = payload.message || 'Failed to accept invitation';
    },

    // Direct relationship creation (admin mode)
    postAdvisorHierarchyRelationshipDirectByAdvisorIdRequest(
      state,
      action: PayloadAction<{
        advisorId: string;
        subordinateAdvisorEmail: string;
        enabledClientAccess: boolean;
      }>,
    ) {
      state.directRelationshipMutationStatus = 'loading';
      state.directRelationshipMutationError = null;
    },
    postAdvisorHierarchyRelationshipDirectByAdvisorIdResponse(
      state,
      action: PayloadAction<HierarchyRelationshipDirectCreatedResponse>,
    ) {
      state.directRelationshipMutationStatus = 'success';
      state.directRelationshipMutationError = null;
    },
    postAdvisorHierarchyRelationshipDirectByAdvisorIdFailure(
      state,
      action: PayloadAction<ApiExError>,
    ) {
      state.directRelationshipMutationStatus = 'error';
      const errorMessage = apiReponseError(action.payload);
      if (apiReponseErrorIncludes(action.payload, 'no advisor found')) {
        state.directRelationshipMutationError =
          'The provided email is not found in the system. In order to assign directly, the person must be an existing advisor.';
      } else if (apiReponseErrorIncludes(action.payload, 'relationship already exists')) {
        state.directRelationshipMutationError = errorMessage ?? 'A relationship already exists between these advisors.';
      } else if (apiReponseErrorIncludes(action.payload, 'cannot create a relationship with yourself')) {
        state.directRelationshipMutationError = 'You cannot create a relationship with yourself.';
      } else {
        state.directRelationshipMutationError =
          errorMessage ?? 'Failed to create relationship directly.';
      }
    },

    // Clear all advisor hierarchy relationships state (used when toggling admin mode off or changing advisor)
    clearAdvisorHierarchyRelationships(state) {
      state.relationships = null;
      state.relationshipsFetchStatus = 'idle';
      state.relationshipsFetchError = null;
      state.supervisorAdvisorRelationships = null;
      state.pendingSupervisorInvitations = null;
      state.invitationMutationStatus = 'idle';
      state.invitationMutationError = null;
      state.deleteMutationStatus = 'idle';
      state.deleteMutationError = null;
      state.directRelationshipMutationStatus = 'idle';
      state.directRelationshipMutationError = null;
      state.invitationAcceptResponse = {
        status: 'idle',
        error: null,
        message: null,
      };
      state.invitationDeclineResponse = {
        status: 'idle',
        error: null,
        message: null,
      };
    },
  },
});

export const {
  fetchSubordinateAdvisorsByAdvisorIdRequest,
  fetchSubordinateAdvisorsByAdvisorIdResponse,
  fetchSubordinateAdvisorsByAdvisorIdFailure,
  postAdvisorHierarchyRelationshipRequest,
  postAdvisorHierarchyRelationshipResponse,
  postAdvisorHierarchyRelationshipFailure,
  postAdvisorHierarchyInvitationByAdvisorIdRequest,
  postAdvisorHierarchyInvitationByAdvisorIdResponse,
  postAdvisorHierarchyInvitationByAdvisorIdFailure,
  deleteAdvisorHierarchyRelationshipByAdvisorIdRequest,
  deleteAdvisorHierarchyRelationshipByAdvisorIdResponse,
  deleteAdvisorHierarchyRelationshipByAdvisorIdFailure,
  fetchAdvisorHierarchyRelationshipsByAdvisorIdRequest,
  fetchAdvisorHierarchyRelationshipsByAdvisorIdResponse,
  fetchAdvisorHierarchyRelationshipsByAdvisorIdFailure,
  postAdvisorHierarchyInvitationDeclineByInvitationIdRequest,
  postAdvisorHierarchyInvitationDeclineByInvitationIdResponse,
  postAdvisorHierarchyInvitationDeclineByInvitationIdFailure,
  postAdvisorHierarchyInvitationAcceptByInvitationIdRequest,
  postAdvisorHierarchyInvitationAcceptByInvitationIdResponse,
  postAdvisorHierarchyInvitationAcceptByInvitationIdFailure,
  postAdvisorHierarchyRelationshipDirectByAdvisorIdRequest,
  postAdvisorHierarchyRelationshipDirectByAdvisorIdResponse,
  postAdvisorHierarchyRelationshipDirectByAdvisorIdFailure,
  clearAdvisorHierarchyRelationships,
} = advisorHierarchySlice.actions;

export const advisorHierarchyReducer = advisorHierarchySlice.reducer;
